<template>
  <div>
    <section-header ref="CONTRAT"
                    :is-extended="true"
                    :dialog="true"
                    :preventClose="shouldAccept"
                    logoClass="CguHeaderLogo"
                    @close-dialog=closeDialog>
    </section-header>

    <div>
      <p class="titre1 mb-2 px-3">
        {{ isSalarie ? 'Chers salariés intermittents,' : 'Chers employeurs,' }}
      </p>
      <p class="dialog-intro px-3" v-html="cguIntro"></p>
    </div>

    <div class="scrollable-body">
      <p class="section-title">Conditions Générales d’Utilisation – Chèque-Intermittents</p>
      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">1. Préambule</p>
      </div>

      <div class="section-paragraph">
        <p>
          La plateforme Chèque-Intermittents est une solution innovante conçue pour simplifier et
          sécuriser la gestion administrative, sociale et financière des employeurs d’intermittents du spectacle. Elle
          propose un guichet unique notamment pour :
        </p>
        <ul>
          <li>l’estimation des coûts salariaux liés aux plateaux artistiques et techniques ;</li>
          <li>la création, la gestion et la signature électronique des contrats de travail (conforme au règlement
            eIDAS 910/2014), ainsi que l’édition des bulletins de paie ;
          </li>
          <li>les inscriptions et déclarations préalables aux organismes sociaux, ainsi que les reversements des
            cotisations sociales (URSSAF, Congés Spectacles, Pôle Emploi Spectacle, AUDIENS, AFDAS, DGFIP, etc.) ;
          </li>
          <li>le versement des salaires aux salariés intermittents ;</li>
          <li>la mise à disposition d’un espace personnel sécurisé pour chaque intermittent, avec accès à ses
            contrats, bulletins de paie, AEM et bordereaux Congés Spectacles.
          </li>
        </ul>

        <p>Les Conditions Générales d’Utilisation (ci-après les "CGU") s’appliquent aux relations entre :</p>
        <ul>
          <li>AGETA, Société par actions simplifiée au capital de 10 000 €, immatriculée au RCS de
            Versailles sous le n° 538 686 254, dont le siège est situé au 1, rue du Prieuré, 78100
            Saint-Germain-en-Laye, éditeur de la Plateforme ;
          </li>
          <li>toute personne physique ou morale souhaitant utiliser les Services de la Plateforme et ayant contracté
            avec AGETA ;
          </li>
          <li>les intermittents du spectacle bénéficiant d’un accès sécurisé à la Plateforme pour consulter, signer,
            télécharger et archiver leurs documents administratifs et sociaux.
          </li>
        </ul>

        <p>
          En souscrivant aux Services, le Client bénéficie d’un accompagnement complet et sécurisé, lui permettant de
          se concentrer sur son activité principale tout en confiant à AGETA la réalisation technique des obligations
          légales liées à l’emploi d’intermittents.
        </p>

        <p>
          Les CGU ont pour objet de définir :
        </p>
        <ul>
          <li>les conditions d’accès et d’utilisation de la Plateforme par les employeurs et les salariés ;</li>
          <li>le cadre du mandat de versement et de reversement confié à AGETA ;</li>
          <li>les modalités tarifaires et les règles de facturation ;</li>
          <li>les garanties de sécurité, de confidentialité et de protection des Données Personnelles, conformément au
            RGPD ;
          </li>
          <li>les droits et obligations des parties, y compris les modalités d’utilisation de la Plateforme par les
            Utilisateurs Salariés.
          </li>
        </ul>

        <p>
          Toute souscription aux Services, y compris l’utilisation d’une offre freemium (gratuite), vaut acceptation
          irrévocable des CGU. Le Client et l’Utilisateur Salarié sont invités à les lire attentivement avant toute
          utilisation de la Plateforme.
        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">2. Définitions</p>
      </div>

      <div class="section-paragraph">
        <p>
          Les termes ci-dessous, utilisés avec une majuscule, ont la signification suivante dans le cadre des CGU :
        </p>
        <table class="cgu-definitions">
          <thead>
          <tr>
            <th>Terme</th>
            <th>Définition</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Abonnement</td>
            <td>Accès aux Services de la Plateforme souscrit par le Client, selon les conditions des CGU.</td>
          </tr>
          <tr>
            <td>CGU</td>
            <td>Les présentes Conditions Générales d’Utilisation de la Plateforme.</td>
          </tr>
          <tr>
            <td>Client</td>
            <td>Personne physique ou morale ayant conclu un Contrat avec AGETA pour utiliser les Services.</td>
          </tr>
          <tr>
            <td>Contrat</td>
            <td>Ensemble des documents contractuels entre les Parties : CGU, conditions particulières, devis,
              avenants.
            </td>
          </tr>
          <tr>
            <td>Données Client</td>
            <td>Données collectées ou générées via la Plateforme, incluant salaires, contrats, déclarations sociales,
              etc.
            </td>
          </tr>
          <tr>
            <td>Données Personnelles</td>
            <td>Informations relatives à une personne physique identifiée ou identifiable, conformément au RGPD.</td>
          </tr>
          <tr>
            <td>Espace Client</td>
            <td>Espace sécurisé affecté au Client pour accéder aux Services.</td>
          </tr>
          <tr>
            <td>Espace Personnel</td>
            <td>Espace sécurisé dédié aux Utilisateurs Salariés pour consulter et archiver leurs documents.</td>
          </tr>
          <tr>
            <td>Identifiant</td>
            <td>Nom ou pseudonyme utilisé pour s’identifier sur la Plateforme, associé à un mot de passe.</td>
          </tr>
          <tr>
            <td>Offres</td>
            <td>Formules d’abonnement proposées par AGETA.</td>
          </tr>
          <tr>
            <td>Partie</td>
            <td>Le Client ou AGETA (individuellement ou conjointement). Les Utilisateurs Salariés ne sont pas
              Parties.
            </td>
          </tr>
          <tr>
            <td>Plateforme</td>
            <td>Solution SaaS éditée par AGETA pour la gestion des obligations légales liées à l’emploi.</td>
          </tr>
          <tr>
            <td>SaaS</td>
            <td>Modèle logiciel par abonnement, hébergé et accessible à distance via Internet.</td>
          </tr>
          <tr>
            <td>Services</td>
            <td>Services proposés dans le cadre de l’Abonnement : contrats, salaires, déclarations sociales, etc.</td>
          </tr>
          <tr>
            <td>Services Complémentaires</td>
            <td>Services optionnels non inclus dans l’Abonnement, disponibles à la souscription.</td>
          </tr>
          <tr>
            <td>Site</td>
            <td>Site internet <a href="https://www.cheque-intermittents.com" target="_blank">www.cheque-intermittents.com</a>.
            </td>
          </tr>
          <tr>
            <td>Solution de Paiement</td>
            <td>Moyens de paiement sécurisés mis à disposition par AGETA : SEPA, virement, carte bancaire.</td>
          </tr>
          <tr>
            <td>Utilisateur Employeur</td>
            <td>Personne physique mandatée par le Client pour utiliser la Plateforme.</td>
          </tr>
          <tr>
            <td>Utilisateur Salarié</td>
            <td>Salarié utilisant la Plateforme pour accéder à ses documents. Non partie au Contrat, mais bénéficiaire
              des Services.
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">3. Objet et Périmètre des Services</p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">3.1. Présentation de la Plateforme</p>
        <p>
          La Plateforme Chèque-Intermittents est une solution complète conçue pour simplifier et sécuriser la gestion
          administrative des employeurs d’intermittents du spectacle. En souscrivant aux Services, le Client bénéficie
          d’un ensemble de fonctionnalités lui permettant de gérer l’intégralité du processus de rémunération et de
          déclaration sociale, depuis la création des contrats de travail jusqu’au paiement des salaires et des
          cotisations sociales, le tout au sein d’un environnement en ligne sécurisé et intuitif.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">3.2. Fonctionnalités et guichet unique</p>
        <p>
          AGETA met à la disposition du Client la Plateforme, qui permet, en mode SaaS, non seulement de générer des
          contrats de travail et des bulletins de paie conformes à la réglementation en vigueur, mais aussi de
          centraliser l’ensemble des opérations financières et administratives liées à l’emploi d’intermittents du
          spectacle. Ce système repose sur un mécanisme de guichet unique, offrant au Client la possibilité de confier
          à AGETA le soin de verser les salaires nets aux intermittents ainsi que de déclarer et reverser les
          cotisations sociales aux organismes sociaux. Ainsi, le Client n’a plus qu’un seul interlocuteur pour
          l’ensemble de ces démarches, ce qui lui permet de gagner un temps précieux et de réduire les risques
          d’erreurs administratives.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">3.3. Mandat et conditions d’exécution</p>
        <p>
          En souscrivant un Contrat, le Client donne mandat à AGETA pour agir en son nom et pour son compte dans le
          cadre du versement des salaires nets aux intermittents et du reversement des cotisations sociales aux
          organismes concernés, sous réserve du règlement préalable par le Client des sommes dues (salaires nets,
          cotisations sociales et frais de service). Ce mandat, qui fait l’objet d’une acceptation explicite lors de
          la souscription, couvre l’ensemble des opérations nécessaires à la bonne exécution des obligations légales
          de l’employeur. AGETA s’engage à effectuer ces versements et déclarations dans le respect des délais légaux
          et avec la plus grande rigueur, tout en fournissant au Client des justificatifs détaillés pour chaque
          transaction effectuée.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">3.4. Publics concernés</p>
        <p>
          La Plateforme s’adresse à un large public d’employeurs, qu’ils soient des professionnels du spectacle
          (entreprises, associations, institutions culturelles) ou des employeurs occasionnels (particuliers,
          collectivités, entreprises en dehors du secteur culturel, etc.). Les intermittents du spectacle bénéficient
          quant à eux d’un accès gratuit à un espace personnel sécurisé leur permettant de consulter et télécharger
          leurs contrats de travail, leurs bulletins de paie, ainsi que les justificatifs de déclaration et de
          paiement des cotisations sociales. Cet accès est conçu pour offrir une transparence totale sur les éléments
          de rémunération et les déclarations sociales, tout en garantissant la confidentialité et la sécurité des
          Données Personnelles.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">3.5. Conditions d’utilisation</p>
        <p>
          L’utilisation des Services proposés par AGETA via la Plateforme est subordonnée à la création d’un compte
          Client, à l’acceptation expresse des CGU, ainsi qu’au paiement des frais liés à l’abonnement et aux services
          utilisés. Le Client reconnaît, en souscrivant aux Services, qu’il reste seul responsable du respect de ses
          obligations légales en matière de droit du travail et de protection sociale, tout en confiant à AGETA la
          réalisation technique des opérations de versement et de déclaration. Cette répartition des responsabilités
          permet au Client de se concentrer sur son activité principale, tout en bénéficiant d’un accompagnement
          sécurisé et conforme aux exigences réglementaires.
        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">4. Mandat de Versement et Reversement</p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">4.1. Octroi du mandat</p>
        <p>
          En souscrivant aux Services, le Client accorde à AGETA un mandat exprès (ci-après le Mandat) pour effectuer,
          en son nom et pour son compte, le versement des salaires nets aux intermittents ainsi que la déclaration et
          le reversement des cotisations sociales aux organismes sociaux. Le Mandat, qui constitue le fondement
          juridique de la relation entre les parties, est encadré par les CGU et fait l’objet d’une acceptation
          formelle de la part du Client lors de la création de son compte.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">4.2. Obligations du Client</p>
        <p>
          Dans le cadre du Mandat, le Client s’engage à verser à AGETA, avant chaque échéance légale, l’intégralité
          des fonds nécessaires au paiement des salaires nets dus aux intermittents, ainsi qu’au reversement des
          cotisations sociales aux organismes sociaux. Ces fonds doivent être transmis dans les délais impartis pour
          permettre à AGETA d’effectuer les versements et déclarations dans les conditions prévues par la
          réglementation. Le Client garantit par ailleurs l’exactitude des informations qu’il communique via la
          Plateforme, notamment en ce qui concerne les montants des salaires bruts, les codes APE, et toute autre
          donnée nécessaire à la bonne exécution du Mandat.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">4.3. Engagements d’AGETA</p>
        <p>
          De son côté, AGETA s’engage à verser les salaires nets aux salariés intermittents dans un délai maximal de
          quarante-huit (48) heures suivant la réception des fonds, et à déclarer et reverser les cotisations sociales
          aux organismes sociaux dans le respect des délais prévus par la réglementation sous réserve d’avoir reçu les
          fonds en temps utile de la part du Client. Pour chaque opération effectuée, AGETA fournit au Client un reçu
          détaillé, lui permettant de justifier auprès des intermittents et des organismes sociaux que les paiements
          et déclarations ont bien été réalisés. Ces justificatifs sont conservés sur la Plateforme pendant toute la
          durée de l’Abonnement du Client, et jusqu’à trois (3) mois après sa résiliation. Passé ce délai, le Client
          est seul responsable de l’archivage de ses documents pour une durée de cinq (5) ans, conformément aux
          obligations légales en matière de conservation des documents sociaux (L3243-4 du Code du travail).
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">4.4. Responsabilités du Client</p>
        <p>
          Le Client reconnaît que, en tant que mandant d’AGETA pour le versement des salaires et le reversement des
          cotisations, il reste seul responsable du respect de ses obligations légales en matière de droit du travail
          et de protection sociale. En cas d’erreur ou d’omission dans les données transmises par le Client via la
          Plateforme, le Client assume l’entière responsabilité des conséquences qui pourraient en découler, y compris
          les éventuelles pénalités ou sanctions. AGETA, pour sa part, s’engage à corriger sans délai toute erreur
          imputable à la Plateforme ou à ses processus, et à prendre en charge uniquement les frais de traitement
          engagés pour régulariser la situation. Le Mandat ne s’étend pas aux Utilisateurs Salariés qui refuseraient
          d’utiliser la Plateforme. Dans ce cas, le Client conserve l’entière responsabilité de la transmission des
          documents, conformément à l’article L3243-2 du Code du travail.
        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">5. Offres d’Abonnement et Facturation</p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">5.1. Formules d’abonnement</p>
        <p>
          AGETA propose plusieurs formules d’abonnement mensuel, avec engagement annuel, adaptées aux besoins et à la
          taille des structures employeuses. Ces Offres, détaillées dans l’annexe ci-dessous, permettent au Client de
          choisir le niveau de service le plus adapté à son activité, tout en bénéficiant d’une facturation
          transparente et adaptée à son utilisation de la Plateforme.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">5.2. Évolution des Offres</p>
        <p>
          Les Offres d’abonnement sont conçues pour évoluer en fonction du volume d’activité du Client. Ainsi, le
          passage d’une Offre à une autre peut être automatique, dès lors que le nombre de bulletins de paie générés
          via la Plateforme dépasse les seuils définis pour l’Offre en cours, ou initié par le Client, lorsqu’il
          souhaite accéder à des services plus étendus ou, au contraire, réduire son niveau d’engagement. Dans tous
          les cas, AGETA informe le Client à chaque changement d’Offre rendu obligatoire par un dépassement de seuil
          tel que défini dans l’annexe ci-dessous.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">5.3. Modèle de facturation</p>
        <p>
          La facturation des services proposés par AGETA repose sur un modèle combinant l’abonnement mensuel et une
          facturation à l’usage, proportionnelle au nombre de bulletins de paie générés via la Plateforme ainsi qu’au
          montant du coût employeur des salaires déclarés. Ce système permet au Client de bénéficier d’une
          tarification adaptée à son volume d’activité tout en s’engageant sur une durée minimale de douze mois, et
          au-delà, renouvelable par périodes successives d’un mois par tacite reconduction.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">5.4. Passage entre Offres</p>
        <p>
          En cas de passage automatique vers une Offre supérieure, lié au dépassement des seuils de bulletins de paie,
          le nouveau tarif est automatiquement appliqué à compter de la facture d’abonnement du mois qui suit cette
          montée.
        </p>
        <br>
        <p>
          À l’inverse, le passage vers une Offre inférieure ou supérieure, effectué à la demande du Client, prend
          effet immédiatement, sous réserve que son utilisation de la Plateforme respecte les conditions de l’Offre
          cible sur une période glissante de 12 mois. Ce passage entraîne un nouvel engagement minimal de douze (12)
          mois pour l’offre choisie, renouvelable par tacite reconduction, conformément aux conditions générales
          d’abonnement définies à l’article 5.3 des présentes CGU. Cette souplesse permet au Client d’ajuster son
          abonnement en fonction de ses besoins réels, sans contrainte inutile.
        </p>
        <br>
        <p>
          Le passage de toute offre payante à l’offre freemium est possible à la demande du Client, à condition que la
          durée minimale d’engagement en cours (initiale ou suite à un changement d’offre effectué à la demande du
          Client) ait été intégralement respectée. Ce passage prend effet dès le mois suivant la demande, sous réserve
          que le Client n’ait aucune facture impayée.L’offre freemium est soumise uniquement à une facturation à
          l’usage, conformément à l’article 5.5, et ne donne accès qu’aux fonctionnalités de base de la Plateforme,
          telles que définies dans l’annexe ci-dessous.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">5.5. Offre « freemium » (gratuite) </p>
        <p>
          L’offre « freemium » permet au Client d’accéder gratuitement aux fonctionnalités de base de la Plateforme,
          sans engagement financier. Cette offre est uniquement soumise à une facturation à l’usage (section 5.3).
          AGETA se réserve le droit de limiter l’accès à certaines fonctionnalités pour les Clients utilisant l’offre
          « freemium ».
        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">6. Modalités Financières</p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">6.1. Processus de paiement</p>
        <p>
          Le paiement des services s’effectue selon un processus simplifié et sécurisé au sein de la Plateforme. Le
          Client verse à AGETA l’intégralité des fonds nécessaires au paiement des salaires nets et des cotisations
          sociales, ainsi que les frais d’abonnement et de facturation à l’usage. AGETA se charge ensuite de
          redistribuer ces fonds aux intermittents et aux organismes sociaux, conformément au Mandat.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">6.2. Calendrier des versements</p>
        <p>
          Les versements sont effectués selon un calendrier précis : les salaires nets sont versés aux intermittents
          sous quarante-huit (48) heures après réception des fonds, tandis que les cotisations sociales sont reversées
          aux organismes sociaux dans le respect des délais règlementaires. AGETA prélève également, à cette occasion,
          les frais liés à l’abonnement et à la facturation à l’usage, conformément aux tarifs en vigueur.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">6.3. Retard et non-paiement des montants sociaux</p>
        <p>
          Un retard de paiement des montants sociaux est inévitable lorsque le Client ne règle pas les salaires et
          cotisations sociales à la date de leurs échéances légales applicables diminuée de temps de traitement
          d’AGETA. AGETA décline toute responsabilité en cas de retard de versement du Client.
          En cas de retard, les éventuelles pénalités et majorations appliquées par les organismes sociaux seront à la
          charge exclusive du Client. Si le retard de paiement ou de déclaration est imputable à AGETA, celle-ci
          prendra en charge, dans le cadre du Mandat dans le cadre du Mandat, les frais inerrants à leur traitement
          administratif.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">6.4. Non-paiement de l’abonnement ou des frais</p>
        <p>
          En cas de non-paiement de l’abonnement ou des frais de traitement, AGETA peut suspendre l’accès à la
          Plateforme dans un délai de quinze (15) jours à compter de la date d’échéance. Si le paiement n’est pas
          régularisé dans ce délai, AGETA se réserve le droit de résilier le Contrat sans préavis supplémentaire.
        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">7. Responsabilités</p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">7.1. Responsabilité d’AGETA</p>

        <p>
          AGETA est responsable de la bonne exécution des Services, notamment en ce qui concerne :
        </p>
        <ul>
          <li>Le versement effectif des salaires nets aux Utilisateurs Salariés, dans les délais impartis ;</li>
          <li>La déclaration et le reversement corrects des cotisations sociales aux organismes sociaux, dans les
            délais impartis ;
          </li>
          <li>La sécurité et la confidentialité des Données Personnelles traitées via la Plateforme, conformément aux
            exigences du RGPD et aux bonnes pratiques en matière de protection des données ;
          </li>
          <li>La disponibilité et la performance de la Plateforme, dans les limites des obligations de moyens qui lui
            incombent.
          </li>
        </ul>

        <p>
          En cas d’erreur imputable à AGETA ou à la Plateforme (par exemple, un dysfonctionnement technique entraînant
          un retard de versement), AGETA s’engage à corriger le problème dans les meilleurs délais et à prendre en
          charge tous les frais administratifs engagés par elle pour régulariser la situation. Cependant, la
          responsabilité d’AGETA ne peut être engagée en cas de force majeure, de défaut d’information ou d’erreur du
          Client, ou de dysfonctionnement des réseaux ou services tiers (par exemple, une panne bancaire ou un
          problème chez un organisme social).
        </p>
        <br>
        <p>
          En toute hypothèse, la responsabilité d’AGETA ne pourra être recherchée qu’en cas de faute avérée de sa part
          et à l’exclusion de toute faute ou de toute négligence qui serait imputable au Client ou à un tiers. Dans
          l’hypothèse où la responsabilité de AGETA serait engagée, l’indemnisation toutes causes confondues,
          principal, intérêts et frais, sera limitée au préjudice direct et prévisible subi par le Client sans pouvoir
          excéder les sommes réglées par ce dernier dans le cadre de son Abonnement au cours des douze (12) mois
          précédant la date de survenance du préjudice.
        </p>
        <br>
        <p>
          En aucun cas AGETA ne pourra être tenue pour responsable tant à l’égard du Client qu’à l’égard de tout
          tiers, pour tout dommage constitué d’une perte d’exploitation, d’une perte de bénéfice ou de réputation
          résultant de l’utilisation ou de l’impossibilité d’utiliser la Plateforme.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">7.2. Responsabilité du Client</p>

        <p>Le Client reste seul responsable :</p>
        <ul>
          <li>De l’exactitude et de la complétude des informations saisies via la Plateforme (montants des salaires,
            codes APE, données des intermittents, etc.).
          </li>
          <li>Du respect des délais légaux pour le versement des salaires et des cotisations sociales.</li>
          <li>De l’utilisation conforme de la Plateforme, notamment en ce qui concerne le respect des CGU et des lois
            applicables.
          </li>
        </ul>

        <p>
          Le Client garantit AGETA contre toute réclamation, action en justice ou sanction résultant d’un retard,
          d’une erreur ou omission de sa part dans les données transmises via la Plateforme, ou d’un manquement à ses
          obligations légales en matière de droit du travail ou de protection sociale.
        </p>
        <br>
        <p>
          En cas de refus par un Utilisateur Salarié d’utiliser la Plateforme pour consulter ou recevoir ses documents
          (bulletins de paie, contrats, etc.), le Client reste seul responsable de lui fournir ces documents par tout
          autre moyen conforme à la loi, sans que cela n’engage la responsabilité d’AGETA. Cependant, AGETA met à
          disposition du Client les outils nécessaires pour exporter les documents concernés.
        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">8. Données Personnelles (RGPD)</p>
      </div>

      <div class="section-paragraph">
        <p>
          AGETA agit en tant que responsable de traitement pour les Données Personnelles du Client, et en tant que
          sous-traitant pour les Données Personnelles des Utilisateurs Salariés, conformément au Règlement Général sur
          la Protection des Données (RGPD).
        </p>
        <br>
        <p>
          Les Données Personnelles collectées via la Plateforme sont hébergées en France, chez un prestataire certifié
          ISO 27001, et sont conservées, sauf accord particulier avec le Client, pendant une durée n’excédant pas
          celle nécessaire à la réalisation des finalités pour lesquelles elles sont traitées (par exemple, cinq (5)
          ans pour les bulletins de paie, conformément aux obligations légales en vigueur – L3243-4 du Code du
          travail).
        </p>
        <br>
        <p>
          Les Utilisateurs Salariés disposent d’un droit d’accès, de rectification, d’effacement et de portabilité de
          leurs Données Personnelles, qu’ils peuvent exercer directement en contactant AGETA. Le Client s’engage à
          informer ses Utilisateurs Salariés de leurs droits et à faciliter l’exercice de ceux-ci.
        </p>
        <br>
        <p>
          Un addendum spécifique (DPA) détaillant les modalités de traitement des Données Personnelles est disponible
          sur demande ou en annexe des CGU.
        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">9. Résiliation et Modification des Services</p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">9.1. Résiliation</p>

        <p>
          Le Client peut résilier son abonnement à la Plateforme à la fin de chaque mois en cours, sous réserve de
          respecter un préavis de quinze (15) jours et la durée minimale d’engagement en cours (initiale ou réengagée
          suite à un passage à une offre inférieure, conformément aux articles 5.3 et 5.4). À défaut de respect de ce
          préavis, la résiliation ne prendra effet qu’à la fin du mois suivant, et le Client reste redevable des frais
          d’abonnement et de facturation à l’usage jusqu’à cette date.
        </p>
        <br>
        <p>
          AGETA peut bloquer l’accès du Client à la Plateforme, sans remboursement ni indemnité, quinze (15) jours
          après une mise en demeure par simple e-mail, message sur l’Espace Personnel du Client ou lettre recommandée
          avec avis de réception, restée infructueuse, notamment en cas de :
        </p>
        <ul>
          <li>Non-paiement des frais d’abonnement ou de facturation à l’usage ;</li>
          <li>Utilisation frauduleuse ou non conforme de la Plateforme ;</li>
          <li>Manquement grave aux CGU.</li>
        </ul>

        <p>
          AGETA pourra en outre résilier le Contrat, pour les mêmes motifs, si elle le souhaite, par lettre
          recommandée avec avis de réception dont la date de première présentation tiendra lieu de date de
          notification, quand bien même le Client serait absent, refuserait le pli ou n’irait pas le retirer à La
          Poste.
        </p>
        <br>
        <p>
          En cas de résiliation, quelle qu’en soit la cause, le Client dispose d’un délai de trente (30) jours pour
          exporter ses données (contrats, bulletins de paie, etc.) depuis la Plateforme. Passé ce délai, AGETA se
          réserve le droit de supprimer définitivement les données du Client, conformément à sa politique de
          conservation.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">9.2. Modification des Offres et des CGU</p>
        <p>
          AGETA peut modifier les seuils, plafonds et tarifs des Offres (détaillés dans l’annexe ci-dessous) ainsi que
          les CGU, sous réserve d’un préavis de trente (30) jours après notification au Client par e-mail et via la
          Plateforme desdites modifications.
          Si les nouvelles conditions entraînent pour le Client un surcoût, il sera libre de ne pas les accepter et il
          pourra résilier son abonnement sans frais avant leur entrée en vigueur.

        </p>
      </div>

      <div class="scroll-header">
        <img src="../../assets/SVG/CguSectionLogo.svg" alt="Section Logo" class="section-logo"/>
        <p class="section-title">10. Dispositions Diverses</p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">10.1. Force majeure</p>

        <p>
          Toute inexécution, exécution partielle ou défectueuse par l’une des Parties de ses obligations au titre du
          Contrat résultant d’un cas de force majeure, au sens de l’article 1218 du Code civil et de la jurisprudence,
          ne constitue pas une violation des obligations du Contrat de la part de cette Partie.
        </p>
        <br>
        <p>
          La survenance d’un tel cas de force majeure peut suspendre l’exécution des obligations des Parties. Pour ce
          faire, la Partie invoquant un cas de force majeure est tenue de le notifier à l’autre Partie par l’envoi
          d’une lettre recommandée avec avis de réception contenant tout élément de toute nature visant à justifier un
          cas de force majeure répondant aux conditions de l’article 1218 du Code civil.
        </p>
        <br>
        <p>
          Si l’empêchement consécutif au dit cas de force majeure perdure plus de deux (2) mois, le Contrat pourra
          être résilié de plein droit et sans préavis par l’une ou l’autre des Parties, sous réserve d’accord
          contraire entre les Parties, par l’envoi d’une lettre recommandée avec accusé de réception contenant tout
          élément de toute nature visant à justifier (i) un cas de force majeure répondant aux conditions de l’article
          1218 du Code civil et (ii) la résiliation du Contrat qui en découle.
        </p>
        <br>
        <p>
          La résiliation du Contrat au motif de la force majeure sera aux risques et périls de la Partie qui en prend
          l’initiative.
        </p>
        <br>
        <p>
          Les Parties conviennent de considérer les pandémies et épidémies comme cas de Force Majeure.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">10.2. Dispositions invalides</p>
        <p>
          Si des dispositions des CGU étaient tenues pour non valides ou déclarées comme telles en application d’un
          traité, d’une loi ou d’un règlement, ou encore à la suite d’une décision définitive d’une juridiction
          compétente, les autres dispositions des CGU garderont toute leur force et leur portée et les Parties se
          rapprocheront afin de convenir d’autres dispositions, valides, d’effet similaire aux dispositions invalides.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">10.3. Renonciation</p>
        <p>
          Aucun acte, délai à agir ou toute autre attitude, passive ou active, de la part de l’une des Parties ne sera
          réputé constituer pour cette Partie une renonciation à l’un quelconque des droits et actions dont elle est
          créancière en application des CGU, à moins que cette renonciation ne soit constatée par un écrit signé par
          un représentant dûment habilité.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">10.4. Loi applicable, litiges</p>

        <p>
          Les CGU sont régies par le droit français.
        </p>
        <br>
        <p>
          En cas de litige entre le Client et AGETA concernant l’exécution des CGU, les Parties s’engagent à tenter de
          régler le différend à l’amiable. À défaut d’accord amiable, le litige sera soumis à la médiation, au sein du
          Centre de Médiation et d’Arbitrage de Paris (CMAP) conformément à son règlement de médiation, avant toute
          action en justice.
        </p>
        <br>
        <p>
          En cas de litige persistant après tentative de médiation, les tribunaux de Versailles seront seuls
          compétents pour en connaître, nonobstant pluralité de défendeurs ou appel en garantie.
        </p>
      </div>

      <div class="section-paragraph">
        <p class="section-subtitle">10.5. Intégralité du contrat</p>
        <p>
          Le Contrat constitue l’intégralité de l’accord entre les Parties et annule et remplace toute convention
          antérieure, écrite ou orale, relative à l’objet du Contrat.
        </p>

        <hr/>

        <p class="section-subtitle" style="text-decoration: underline;">Annexe : Détail des Offres et Seuils</p>

        <table class="offer-table">
          <thead>
          <tr>
            <th>Offre</th>
            <th>Abonnement</th>
            <th>Seuils (bulletins/an)</th>
            <th>Facturation à l'usage</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>PRELUDE</td>
            <td>Gratuite (freemium)</td>
            <td>Illimité</td>
            <td>Oui</td>
          </tr>
          <tr>
            <td>PIANO</td>
            <td>Payante</td>
            <td>Maximum 24</td>
            <td>Oui</td>
          </tr>
          <tr>
            <td>MEZZO</td>
            <td>Payante</td>
            <td>Maximum 60</td>
            <td>Oui</td>
          </tr>
          <tr>
            <td>FORTE</td>
            <td>Payante</td>
            <td>Illimité</td>
            <td>Oui</td>
          </tr>
          </tbody>
        </table>
        <br>
        <p class="section-subtitle">Règles de passage automatique :</p>
        <ul>
          <li>De PIANO à MEZZO : si le Client génère plus de 24 bulletins de paie
            sur une période glissante de 12 mois.
          </li>
          <li>De MEZZO à FORTE : si le Client génère plus de 60 bulletins de paie
            sur une période glissante de 12 mois.
          </li>
        </ul>

        <p class="section-subtitle">Règles de passage à la demande :</p>
        <ul>
          <li>De PIANO à MEZZO, de PIANO à FORTE,
            de MEZZO à FORTE, de PRELUDE à PIANO,
            MEZZO ou FORTE : à tout moment sur simple choix du Client.
          </li>
          <li>De MEZZO à PIANO : si le Client a généré 24 bulletins de paie ou moins
            sur une période glissante de 12 mois précédant la demande de changement.
          </li>
          <li>De FORTE à MEZZO : si le Client a généré 60 bulletins de paie ou moins
            sur une période glissante de 12 mois précédant la demande de changement.
          </li>
          <li>De FORTE, MEZZO ou PIANO à PRELUDE :
            à tout moment, sous réserve que la durée minimale d’engagement (initiale ou suite à un changement d’offre
            effectué à la demande du Client) ait été intégralement respectée.
          </li>
        </ul>
      </div>
    </div>

    <div v-if="shouldAccept">
      <check-field
          ref="CHECK"
          :data-table="true"
          :edit-field-info=checkFieldInfo()
          :value="cguAccepted"
          :disabled="checkFieldDisabled"
          :context-id="0"
          @updated=setAccepted>
      </check-field>
      <div class="button-wrapper">
        <loader-button
            label="J'ai lu et j'accepte"
            :disabled=!cguAccepted
            :full-width="true"
            @callback="acceptCgu()">
        </loader-button>
      </div>
    </div>
    <div v-else>
      <div class="button-wrapper">
        <loader-button
            label="Fermer"
            :disabled=false
            :full-width="true"
            @callback="closeDialog()">
        </loader-button>
      </div>
    </div>
  </div>

</template>
<script lang="ts">
import {defineComponent} from "vue";
import CheckField from "@/framework/base/components/CheckField.vue";
import {EditFieldType, FieldInfo} from "@/framework/base/components/FieldInfo.ts";
import Ressource from "@/framework/ressource/classes/Ressource.ts";
import LoaderButton from "@/framework/base/components/LoaderButton.vue";
import SectionHeader from "@/framework/base/components/SectionHeader.vue";
import {storeSession, storeUser} from "@/generated/stores";
import UserProfile, {EnumProfile} from "@/framework/base/classes/UserProfile.ts";
import User from "@/generated/classes/User";

export default defineComponent({
  components: {SectionHeader, LoaderButton, CheckField},
  props: {
    data: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      showDialog: false,
      cguAccepted: false,
      checkFieldDisabled: false,
    };
  },
  computed: {
    cguIntro(): string {
      if (this.isSalarie) {
        return "Au moins l’un de vos employeurs utilise la Plateforme Chèque-Intermittents pour gérer vos contrats et bulletins de paie dans le respect de l’Article L3243-2 du Code du Travail.<br>" +
            "Les Conditions Générales d’Utilisation (CGU) ci-dessous encadrent principalement la relation entre votre employeur et Chèque-Intermittents.<br>" +
            "Pour vous, seuls les articles relatifs à votre espace personnel sécurisé, à l’accès à vos documents (contrats, bulletins de paie, AEM…) et à la protection de vos données personnelles vous concernent directement:<br>" +
            "- Article 3.4 - Publics concernés<br>" +
            "- Article 4.4 - Mandat de versement et de reversement - Responsabilité du Client<br>" +
            "- Article 7.2 – Responsabilité<br>" +
            "- Article 8 – Données personnelles (RGPD)<br>" +
            "Bien que vous bénéficiez de services qui vous sont dédiés, dans le respect de vos droits et de la réglementation en vigueur, vous n’êtes pas partie prenante du contrat entre votre employeur et Chèque-Intermittents.<br>" +
            "Pour toute question, contactez votre employeur ou info@cheque-intermittents.com";
      } else {
        return "Ces Conditions Générales d’Utilisation (CGU) définissent le cadre de notre collaboration pour vous accompagner dans la gestion administrative, sociale et financière de vos équipes d’intermittents." +
            "Elles précisent vos droits et obligations, ainsi que ceux de Chèque-Intermittents, afin de vous offrir un service fluide, sécurisé et conforme à la réglementation.<br>" +
            "Notre objectif : vous permettre de vous concentrer sur votre activité, en toute sérénité.<br>" +
            "Pour confirmer votre adhésion aux CGU, merci de cocher la case prévue à la fin de ce document.";
      }
    },
    user (): User | null {
      const connectedUser =  storeSession.connectedUser;
      if (connectedUser && connectedUser.id > 0) {
        const user = storeUser.getUser(connectedUser.id);
        if (user) {
          return user;
        }
      }
      return null;
    },
    isSalarie (): boolean {
      let res = false;
      if (this.user) {
        res = UserProfile.hasProfile(this.user, EnumProfile.salarie);
      }
      return res;
    },
    onboardingCguAccepted (): boolean {
      return !!(this.data as any)?.cguAcceptedAt;
    },
    shouldAccept (): boolean {
      return !this.onboardingCguAccepted && (!this.user || !this.user.cguAcceptedAt);
    },
  },
  methods: {
    checkFieldInfo(): FieldInfo {
      const ressource = new Ressource();
      if (this.isSalarie) {
        ressource.label = "En cochant cette case, je déclare avoir pris connaissance des conditions générales d’utilisation de la plateforme du Chèque-Intermittents me concernant et les accepter";
      } else {
        ressource.label = "En cochant cette case, je déclare avoir pris connaissance des conditions générales d’utilisation de la plateforme du Chèque-Intermittents et les accepter";
      }
      const type = EditFieldType.STRING;
      const precision = 0;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live);
    },
    setAccepted(value: boolean) {
      this.cguAccepted = value;
    },
    acceptCgu() {
      if (this.user && this.user.id > 0) {
        User.cguAccepted().then(() => {
          this.$emit("closeDialog");
        });
      } else {
        this.$emit("updated");
      }
    },
    closeDialog() {
      this.$emit("closeDialog");
    },
  }
})
</script>

<style scoped>
.dialog-intro {
  font-size: 14px;
  color: #135253;
}

.scrollable-body {
  padding: 16px;
  font-size: 15px;
  line-height: 1.6;
  color: #555;
}

.scroll-header {
  display: flex;
  align-items: center;
}

.section-logo {
  width: 16px;
  height: 13px;
  object-fit: contain;
  margin-right: 12px;
  margin-bottom: 10px;
}

.section-title {
  font-size: 1.3rem;
  font-weight: 500;
  color: #171A1C;
}

.section-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: #171A1C;
}

.section-paragraph {
  font-size: 14px;
  color: #135253;
  padding: 0 32px 32px 32px;
}

.section-paragraph p {
  margin: 0;
}

.button-wrapper {
  padding: 32px
}

.cgu-definitions td {
  vertical-align: top;
}

.offer-table th,
.offer-table td {
  padding: 0.75rem 1.5rem;
  text-align: left;
}

</style>
